// Full-screen /view/:id viewer (plan §4A/§8) — near-chromeless anthracite bar
// hosting the sandboxed cross-origin iframe. THIS page (portal origin, holds
// the session) mints the single-use 90 s capability token and re-mints it on
// reload — the token only ever lives in the iframe src, never in the address
// bar, history, or a bookmark. sandbox = allow-scripts + allow-downloads ONLY
// (plan invariant) — never allow-same-origin.
import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, RotateCw, FileWarning, ArrowLeft } from 'lucide-react';
import type { ViewUrlResponse, ViewerDocument } from '@ext-portal/shared';
import { apiFetch, ApiError } from '@/lib/api-client';

type ViewState =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | { status: 'ready'; src: string; nonce: number };

export function Viewer() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('viewer');
  const [state, setState] = useState<ViewState>({ status: 'loading' });
  const [title, setTitle] = useState('');
  const [frameLoaded, setFrameLoaded] = useState(false);

  const mint = useCallback(async () => {
    if (!id) return;
    setFrameLoaded(false);
    setState({ status: 'loading' });
    try {
      const res = await apiFetch<ViewUrlResponse>(`/documents/${id}/view-url`);
      setState({ status: 'ready', src: res.url, nonce: Date.now() });
    } catch (err) {
      if (err instanceof ApiError) {
        setState({ status: 'unavailable' });
      } else {
        setState({ status: 'unavailable' });
      }
    }
  }, [id]);

  useEffect(() => {
    void mint();
  }, [mint]);

  // Best-effort title for the slim bar (the dashboard list is cheap to query).
  useEffect(() => {
    apiFetch<{ data: ViewerDocument[] }>('/me/documents')
      .then((res) => {
        const doc = res.data.find((d) => d.id === id);
        if (doc) {
          setTitle(doc.title);
          document.title = `${doc.title} — UniPortal`;
        }
      })
      .catch(() => {});
    return () => {
      document.title = 'UniPortal — Unifiber';
    };
  }, [id]);

  function close() {
    // Opened in a new tab from the dashboard — try to close it; if the tab
    // wasn't script-opened the browser refuses and we fall back to navigation.
    window.close();
    setTimeout(() => void navigate('/'), 150);
  }

  return (
    <div className="h-dvh flex flex-col bg-[#221f26]">
      {/* Slim chrome bar */}
      <header className="h-11 shrink-0 flex items-center gap-3 px-3 sm:px-4">
        <span className="w-2 h-2 rounded-full bg-brand-gradient shrink-0" aria-hidden="true" />
        <h1 className="text-[13px] font-medium text-white/85 truncate flex-1">{title || 'UniPortal'}</h1>
        <button
          type="button"
          onClick={() => void mint()}
          title={t('reload')}
          className="p-1.5 rounded-md text-white/40 hover:text-white hover:bg-white/10 transition-colors"
        >
          <RotateCw className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={close}
          title={t('common:close')}
          className="p-1.5 rounded-md text-white/40 hover:text-white hover:bg-white/10 transition-colors"
        >
          <X className="w-[18px] h-[18px]" />
        </button>
      </header>

      {/* Gradient hairline — animates while the document loads */}
      {state.status === 'loading' || (state.status === 'ready' && !frameLoaded) ? (
        <div className="loading-hairline shrink-0" />
      ) : (
        <div className="h-[2px] shrink-0 bg-white/10" />
      )}

      <div className="relative flex-1 bg-white">
        {state.status === 'unavailable' && (
          <div className="absolute inset-0 flex items-center justify-center paper-wash">
            <div className="text-center max-w-sm px-6 animate-scale-in">
              <div className="inline-flex p-[2px] rounded-2xl bg-brand-gradient mb-5">
                <div className="p-4 rounded-2xl bg-white">
                  <FileWarning className="w-8 h-8 text-brand-purple" />
                </div>
              </div>
              <h2 className="text-lg font-semibold text-ink mb-1.5">{t('unavailableTitle')}</h2>
              <p className="text-sm font-light text-slate-500 mb-6">{t('unavailableBody')}</p>
              <div className="flex items-center justify-center gap-3">
                <button type="button" onClick={() => void navigate('/')} className="btn-quiet">
                  <ArrowLeft className="w-4 h-4" />
                  {t('backToDocuments')}
                </button>
                <button type="button" onClick={() => void mint()} className="btn-gradient">
                  <RotateCw className="w-4 h-4" />
                  {t('reload')}
                </button>
              </div>
            </div>
          </div>
        )}

        {(state.status === 'loading' || (state.status === 'ready' && !frameLoaded)) && (
          <div className="absolute inset-0 flex items-center justify-center bg-white">
            <div className="text-center">
              <div className="w-9 h-9 mx-auto rounded-full border-[3px] border-slate-200 border-t-brand-rose animate-spin" />
              <p className="mt-4 text-xs font-light text-slate-400">{t('loading')}</p>
            </div>
          </div>
        )}

        {state.status === 'ready' && (
          <iframe
            key={state.nonce}
            src={state.src}
            title={title || 'Document'}
            // SECURITY INVARIANT (plan §8): allow-scripts + allow-downloads ONLY
            // — never allow-same-origin / top-navigation / popups / forms / modals.
            // allow-downloads (2026-09-22) is what lets a report's inline JS save a
            // file it built locally (Export to Excel: SheetJS → Blob → <a download>);
            // it grants no origin, no cookies, no network. The same token must be in
            // the content-origin CSP `sandbox` directive (deploy/nginx/content.template)
            // — the browser enforces the UNION of both sandboxes.
            sandbox="allow-scripts allow-downloads"
            referrerPolicy="no-referrer"
            onLoad={() => setFrameLoaded(true)}
            className="absolute inset-0 w-full h-full border-0"
          />
        )}
      </div>
    </div>
  );
}
